import React, { useEffect, useRef, useState } from 'react';
import Pdf from 'react-native-pdf';
import { Document as PdfDocument, Page as PdfPage, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import styled from 'styled-components';
import { contentUrl, isWeb } from '../../../api';
import CenteredIndicator from '../../general/CenteredIndicator';
import { ScrollView } from '../../general/ScrollView';
import {windowIsWeb, windowIsWebDesktop} from '../../../styled-components/windowIsWeb';
import {
  withContentRevalidation,
  withContentRevision,
} from '../../../utils/contentAssetUrl';

// TODO: move this to our own CDN but this should be safe for now
pdfjs.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.5.207/pdf.worker.min.js';

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-left: 16px;
  padding-right: 16px;
  ${windowIsWeb && !windowIsWebDesktop &&
  `
    padding-bottom: 142px;
  `};
  ${windowIsWeb && windowIsWebDesktop &&
  `
    padding-bottom: 54px;
  `};
`;

const PaginationWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  padding-top: 1rem;
  padding-bottom: 1rem;
`;

const ButtonWrapper = styled.button`
  margin-left: 0.5rem;
  margin-right: 0.5rem;
`;

const ErrorWrapper = styled.div`
  padding: 24px;
  color: #c62828;
  background: #ffffff;
`;

const WebPdfWrapper = styled.div`
  max-width: 80vw;
  overflow: scroll;
  margin: 0 auto;
`;

const MobilePdfWrapper = styled(Pdf)`
  flex-grow: 1;
  width: 100%;
`;

interface PDFContentProps {
  content: any;
  planId: string;
  contentSignature: string;
  contentSignatureError?: string;
  contentSignatureLoading: boolean;
};

type WebPdfFile = {
  data: Uint8Array;
  docBaseUrl: string;
};

const SCALE_FACTOR = 0.25;
const PDF_REVALIDATION_DELAY = 4000;
const WebPdfPage = PdfPage as React.ComponentType<any>;

const getResponseRevision = (response: Response): string | undefined =>
  response.headers.get('etag') ||
  response.headers.get('last-modified') ||
  undefined;

const fingerprintPdf = async (
  arrayBuffer: ArrayBuffer,
  responseRevision?: string,
): Promise<string> => {
  if (responseRevision) {
    return responseRevision;
  }

  if (window.crypto && window.crypto.subtle) {
    const digest = await window.crypto.subtle.digest('SHA-256', arrayBuffer);
    return Array.from(new Uint8Array(digest))
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  return `${arrayBuffer.byteLength}`;
};

const PDFContent = ({
  content,
  planId,
  contentSignature,
  contentSignatureError,
  contentSignatureLoading,
}: PDFContentProps) => {
  const basePdfUrl = `${contentUrl}/${planId}/${content.contentID}/${content.file!}${contentSignature}`;
  const pdfUrl = isWeb
    ? withContentRevision(basePdfUrl, content.lastUpdate)
    : basePdfUrl;
  const [numPages, setNumPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [zoomScale, setZoomScale] = useState(1);
  const [webPdfFile, setWebPdfFile] = useState<WebPdfFile>();
  const [webPdfError, setWebPdfError] = useState<string>();
  const webPdfWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isWeb || contentSignatureLoading) {
      return undefined;
    }

    let cancelled = false;
    let lastAppliedRequest = 0;
    let loadedFingerprint: string | undefined;
    let nextRequest = 0;
    let pendingRequests = 0;
    let revalidationExhausted = false;
    let lastLoadError: Error | null = null;
    let loadErrorNotified = false;
    const loadToken = Date.now().toString();
    const revalidationTimers: number[] = [];

    setWebPdfFile(undefined);
    setWebPdfError(undefined);

    if (contentSignatureError || !contentSignature) {
      setWebPdfError(
        contentSignatureError || 'Unable to load content signature',
      );
      return undefined;
    }

    const maybeNotifyLoadError = () => {
      if (
        !cancelled &&
        revalidationExhausted &&
        pendingRequests === 0 &&
        lastAppliedRequest === 0 &&
        lastLoadError &&
        !loadErrorNotified
      ) {
        loadErrorNotified = true;
        setWebPdfError(lastLoadError.message || 'Unable to load PDF');
      }
    };

    const fetchPdf = (validateFirst = false) => {
      const requestNumber = ++nextRequest;
      const requestUrl = withContentRevalidation(
        pdfUrl,
        loadToken,
        requestNumber,
      );
      pendingRequests += 1;
      const fetchFullPdf = () => fetch(requestUrl, {cache: 'no-store'});
      const responsePromise =
        validateFirst && loadedFingerprint
          ? fetch(requestUrl, {cache: 'no-store', method: 'HEAD'})
              .then(response => {
                const responseRevision = response.ok
                  ? getResponseRevision(response)
                  : undefined;

                return responseRevision &&
                  responseRevision === loadedFingerprint
                  ? null
                  : fetchFullPdf();
              })
              .catch(() => fetchFullPdf())
          : fetchFullPdf();

      responsePromise
        .then(async response => {
          if (response === null) {
            return {notModified: true as const};
          }

          if (!response.ok) {
            throw new Error(`Unable to load PDF (${response.status})`);
          }

          const arrayBuffer = await response.arrayBuffer();
          const responseRevision = getResponseRevision(response);

          return {
            arrayBuffer,
            fingerprint: await fingerprintPdf(arrayBuffer, responseRevision),
            notModified: false as const,
          };
        })
        .then(result => {
          if (cancelled || requestNumber <= lastAppliedRequest) {
            return;
          }

          lastAppliedRequest = requestNumber;

          if (
            result.notModified ||
            result.fingerprint === loadedFingerprint
          ) {
            return;
          }

          loadedFingerprint = result.fingerprint;
          setWebPdfError(undefined);
          setWebPdfFile({
            data: new Uint8Array(result.arrayBuffer),
            docBaseUrl: pdfUrl,
          });
        })
        .catch(error => {
          lastLoadError =
            error instanceof Error ? error : new Error('Unable to load PDF');
        })
        .finally(() => {
          pendingRequests -= 1;
          maybeNotifyLoadError();
        });
    };

    fetchPdf();
    revalidationTimers.push(
      window.setTimeout(() => {
        revalidationExhausted = true;
        fetchPdf(true);
      }, PDF_REVALIDATION_DELAY),
    );

    return () => {
      cancelled = true;
      revalidationTimers.forEach(timer => window.clearTimeout(timer));
    };
  }, [contentSignature, contentSignatureError, contentSignatureLoading, pdfUrl]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(currentPage =>
      Math.max(1, Math.min(currentPage, numPages)),
    );
  };

  const appendContentSignature = (href: string) => {
    if (!contentSignature) {
      return href;
    }

    try {
      const url = new URL(href);
      const contentBaseUrl = new URL(contentUrl || '');

      if (
        url.origin === contentBaseUrl.origin &&
        url.pathname.startsWith(contentBaseUrl.pathname) &&
        !url.search
      ) {
        return withContentRevision(
          `${url.origin}${url.pathname}${contentSignature}${url.hash}`,
          content.lastUpdate,
        );
      }
    } catch (e) {
      return href;
    }

    return href;
  };

  const onRenderAnnotationLayerSuccess = () => {
    const annotationLayer = webPdfWrapperRef.current;
    if (!annotationLayer) {
      return;
    }

    annotationLayer.querySelectorAll('a[href]').forEach((link) => {
      link.setAttribute(
        'href',
        appendContentSignature(link.getAttribute('href') || ''),
      );
    });
  };

  const onDocumentItemClick = ({
    pageNumber,
  }: {
    pageNumber: number | string;
  }) => {
    const nextPageNumber = Number(pageNumber);

    if (nextPageNumber >= 1 && nextPageNumber <= numPages) {
      setPageNumber(nextPageNumber);
    }
  };

  const previousPage = () => setPageNumber(pageNumber - 1);
  const nextPage = () => setPageNumber(pageNumber + 1);

  const zoomIn = () => setZoomScale(zoomScale + SCALE_FACTOR);
  const zoomReset = () => setZoomScale(1);
  const zoomOut = () => setZoomScale(zoomScale - SCALE_FACTOR);

  const pagination = () => {
    if (numPages <= 1) {
      return (
        <PaginationWrapper>
          <ButtonWrapper type="button" onClick={zoomOut} disabled={zoomScale - SCALE_FACTOR <= 0}>
            Zoom Out
          </ButtonWrapper>
          <ButtonWrapper type="button" onClick={zoomReset}>
            Reset
          </ButtonWrapper>
          <ButtonWrapper type="button" onClick={zoomIn} disabled={zoomScale - SCALE_FACTOR >= 4}>
            Zoom In
          </ButtonWrapper>
        </PaginationWrapper>
      )
    };

    return (
      <PaginationWrapper>
        <ButtonWrapper
          type="button"
          disabled={pageNumber <= 1}
          onClick={previousPage}
        >
          Previous
        </ButtonWrapper>
        <span>
          Page {pageNumber || (numPages ? 1 : '--')} of {numPages || '--'}
        </span>
        <ButtonWrapper
          type="button"
          disabled={pageNumber >= numPages}
          onClick={nextPage}
        >
          Next
        </ButtonWrapper>
        <ButtonWrapper type="button" onClick={zoomOut} disabled={zoomScale - SCALE_FACTOR <= 0}>
          Zoom Out
        </ButtonWrapper>
        <ButtonWrapper type="button" onClick={zoomReset}>
          Reset
        </ButtonWrapper>
        <ButtonWrapper type="button" onClick={zoomIn} disabled={zoomScale - SCALE_FACTOR >= 4}>
          Zoom In
        </ButtonWrapper>
      </PaginationWrapper>
    );
  };

  if (isWeb) {
    if (contentSignatureLoading || (!webPdfFile && !webPdfError)) {
      return <CenteredIndicator />;
    }

    return (
      <ScrollView>
        <ContentWrapper>
          {pagination()}
          <WebPdfWrapper ref={webPdfWrapperRef}>
            {webPdfError ? (
              <ErrorWrapper>{webPdfError}</ErrorWrapper>
            ) : (
              <PdfDocument
                externalLinkTarget="_blank"
                file={webPdfFile}
                onItemClick={onDocumentItemClick}
                onLoadSuccess={onDocumentLoadSuccess}
              >
                <WebPdfPage
                  onRenderAnnotationLayerSuccess={onRenderAnnotationLayerSuccess}
                  pageNumber={pageNumber}
                  renderAnnotationLayer={true}
                  scale={zoomScale}
                />
              </PdfDocument>
            )}
          </WebPdfWrapper>
          {pagination()}
        </ContentWrapper>
      </ScrollView>
    );
  }

  return (
    <MobilePdfWrapper
      source={{
        uri: pdfUrl,
      }}
      fitPolicy={0}
      style={{
        backgroundColor: 'rgb(241, 243, 254)',
        paddingBottom: 100,
      }}
    />
  );
};

export default PDFContent;
